// API Constants - Centralized API routes and endpoints

// ============================================
// API BASE ROUTES
// ============================================

export const API_ROUTES = {
  MANAGER_V1: '/managerv1',
  MANAGER_REGISTER: import.meta.env.VITE_MANAGER_ROUTE || 'headOfficeAuthV143',
}

// ============================================
// API VERSIONS / RESOURCE PATHS
// ============================================

export const API_VERSIONS = {
  DASHBOARD: 'overviewV1',
  DOCLINK_DATA: 'doclinkV1',
  QUDA_DATA: 'qudaV1',
}

// ============================================
// DASHBOARD ENDPOINTS
// ============================================

export const DASHBOARD_ENDPOINTS = {
  MAIN_DATA: 'ChemistData_All',
  CHEMIST_DATA: 'ChemistData_Single', // Append /{chemistID}
  CHEMIST_SUBS: 'ChemistSubs',
}

// ============================================
// CHART DATA ENDPOINTS
// ============================================

export const CHART_ENDPOINTS = {
  DOCLINK_DASHBOARD: 'ChemistData_All',
}

export const QUDA_ENDPOINTS = {
  QUDA_DASHBOARD: 'ChemistData_All',
  ITEM_ORDERS: 'ItemOrders', // Append /PHUUID/ItemName/DateStart/DateEnd
  SUPPLIER_ORDERS: 'SupplierOrders',
  SUPPLIER_PHARMACIES: 'SupplierPharmacies',
}

// ============================================
// AUTH ENDPOINTS
// ============================================

export const AUTH_ENDPOINTS = {
  MANAGER_LOGIN: 'ManagerLogin',
  VALIDATE_MANAGER: 'ValidateManager',
  LOGIN_WITH_GOOGLE: 'LoginWithGoogle',
  CREATE_USER_WITH_GOOGLE: 'CreateUserWithGoogle',
  LINK_GOOGLE_ACCOUNT: 'LinkGoogleAccount',
  CHECK_USER: 'CheckUser',
}

// ============================================
// HELPER FUNCTIONS TO BUILD FULL PATHS
// ============================================

/**
 * Build a dashboard API path
 * @param {string} endpoint - The dashboard endpoint (e.g., DASHBOARD_ENDPOINTS.MAIN_DATA)
 * @param {string|number} [id] - Optional ID to append to path
 * @returns {string} Full API path
 */
export const buildDashboardPath = (endpoint, id = null) => {
  const base = `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.DASHBOARD}/${endpoint}`
  return id ? `${base}/${id}` : base
}

/**
 * Build a chart data API path
 * @param {string} endpoint - The chart endpoint (e.g., CHART_ENDPOINTS.DOCLINK_DASHBOARD)
 * @returns {string} Full API path
 */
export const buildChartPath = (endpoint) => {
  return `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.DOCLINK_DATA}/${endpoint}`
}

export const buildQudaPath = (endpoint) => {
  return `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.QUDA_DATA}/${endpoint}`
}

/**
 * Build an auth/manager API path
 * @param {string} endpoint - The auth endpoint (e.g., AUTH_ENDPOINTS.MANAGER_LOGIN)
 * @returns {string} Full API path
 */
export const buildAuthPath = (endpoint) => {
  return `/${API_ROUTES.MANAGER_REGISTER}/${endpoint}`
}

// ============================================
// COMMONLY USED FULL PATHS (for convenience)
// ============================================

export const API_PATHS = {
  // Dashboard paths
  MAIN_DATA: buildDashboardPath(DASHBOARD_ENDPOINTS.MAIN_DATA),
  CHEMIST_DATA: buildDashboardPath(DASHBOARD_ENDPOINTS.CHEMIST_DATA),
  CHEMIST_SUBS: buildDashboardPath(DASHBOARD_ENDPOINTS.CHEMIST_SUBS),
  
  // Chart data paths
  DOCLINK_DASHBOARD: buildChartPath(CHART_ENDPOINTS.DOCLINK_DASHBOARD),
  QUDA_DASHBOARD: buildQudaPath(QUDA_ENDPOINTS.QUDA_DASHBOARD),
  ITEM_ORDERS: `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.QUDA_DATA}/${QUDA_ENDPOINTS.ITEM_ORDERS}`,
  SUPPLIER_ORDERS: `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.QUDA_DATA}/${QUDA_ENDPOINTS.SUPPLIER_ORDERS}`,
  SUPPLIER_PHARMACIES: `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.QUDA_DATA}/${QUDA_ENDPOINTS.SUPPLIER_PHARMACIES}`,
  
  // Auth paths
  MANAGER_LOGIN: buildAuthPath(AUTH_ENDPOINTS.MANAGER_LOGIN),
  VALIDATE_MANAGER: buildAuthPath(AUTH_ENDPOINTS.VALIDATE_MANAGER),
  LOGIN_WITH_GOOGLE: buildAuthPath(AUTH_ENDPOINTS.LOGIN_WITH_GOOGLE),
  CREATE_USER_WITH_GOOGLE: buildAuthPath(AUTH_ENDPOINTS.CREATE_USER_WITH_GOOGLE),
  LINK_GOOGLE_ACCOUNT: buildAuthPath(AUTH_ENDPOINTS.LINK_GOOGLE_ACCOUNT),
  CHECK_USER: buildAuthPath(AUTH_ENDPOINTS.CHECK_USER),
}

// ============================================
// USAGE EXAMPLES
// ============================================

/*
// Example 1: Using the full paths directly
import { API_PATHS } from '@/config/apiConstants'
const response = await phappsRequest(API_PATHS.DOCLINK_DASHBOARD, "GET")

// Example 2: Using builder functions for dynamic paths
import { buildDashboardPath, DASHBOARD_ENDPOINTS } from '@/config/apiConstants'
const path = buildDashboardPath(DASHBOARD_ENDPOINTS.CHEMIST_DATA, 123)
const response = await phappsRequest(path, "GET")

// Example 3: Using auth paths
import { API_PATHS } from '@/config/apiConstants'
const response = await apiRequest(API_PATHS.MANAGER_LOGIN, 'POST', { username, password })

// Example 4: Building custom paths
import { API_ROUTES, API_VERSIONS } from '@/config/apiConstants'
const customPath = `${API_ROUTES.MANAGER_V1}/${API_VERSIONS.DASHBOARD}/CustomEndpoint`
*/
